import React from "react";
import { Controller, useForm } from "react-hook-form";
import { toast } from "react-toastify";
import { FileDownloadOutlined } from "@mui/icons-material";
import { IconButton, Tooltip, Box, Grid } from "@mui/material";
import AppConfirmDialog from "components/Shared/AppConfirmDialog";
import AppForm from "components/Shared/AppForm";
import DateRangeToggle from "components/Shared/DateRangeToggle";
import UserSearchAutocomplete from "./UserSearchAutocomplete";
import { exportExcelFile } from "utils/exportExcelFile";
import moment from "moment";
import { useLazyGetVendorProfileListForExportQuery } from "store/services/vendorProfileApi";
const EXCEL_DATA_INITIAL_STATE = {
  dateRange: [],
  responsibleUser: null,
  addedByUser: null,
  referredByUser: null,
  relationshipManagerUser: null,
};

const ExportVendorProfileExcel = ({ isBusy = false, status = "Active" }) => {
  const [showDialog, setShowDialog] = React.useState(false);
  const [setShowVendorForm] = React.useState(false);
  const { control, handleSubmit, reset } = useForm({
    defaultValues: EXCEL_DATA_INITIAL_STATE,
  });
  // RTK Query implementation
  const [fetchVendorProfiles] = useLazyGetVendorProfileListForExportQuery();
  const handleDownload = async (data) => {
    const {
      dateRange,
      responsibleUser,
      addedByUser,
      referredByUser,
      relationshipManagerUser,
    } = data;
    const hasDateRange =
      Array.isArray(dateRange) &&
      dateRange.length === 2 &&
      dateRange[0] &&
      dateRange[1];
    const payload = {
      status,
      onBoardedBy: responsibleUser?._id || null,
      addedBy: addedByUser?._id || null,
      referredBy: referredByUser?._id || null,
      relationshipManager: relationshipManagerUser?._id || null,
      ...(hasDateRange && {
        fromDate: moment(dateRange[0]).format("YYYY-MM-DD"),
        toDate: moment(dateRange[1]).format("YYYY-MM-DD"),
      }),
    };

    try {
      const responseData = await fetchVendorProfiles(payload).unwrap();

      if (!responseData?.data || responseData.data.length === 0) {
        toast.info(
          "No vendor profile records found for the selected date range and filters."
        );
        return;
      }

      const formattedData = responseData?.data.map((row, index) => ({
        "Owner Name": row.ownerName || "N/A",
        "Shop Name": row.shopName || "N/A",
        "Address": row.address || "N/A",
        Email: row.email || "N/A",
        Phone: row.phone || "N/A",
        "Onboarded By": row.onboardedBy || "N/A",
        "Referred By": row.referredBy || "N/A",
        "Relationship Manager": row.relationshipManager || "N/A",
        "Added By": row.addedUser || "N/A",
        "Assigned Branch": row.serviceBranches?.length > 0 ? row.serviceBranches.join(", ") : "N/A",
        "Onboarded Date": row.createdAt
          ? moment(row.createdAt).format("YYYY-MM-DD")
          : "N/A",
      }));

      exportExcelFile({
        sheetData: formattedData,
        fileName: "Onboarded_Vendor_Profiles",
      });
      toast.success("Data downloaded successfully");
    } catch (error) {
      toast.error("Error fetching vendor profile data: " + error.message);
    }
    handleReset();
  };

  const handleReset = () => {
    setShowDialog(false);
    setShowVendorForm(false);
    reset(EXCEL_DATA_INITIAL_STATE);
  };

  return (
    <>
      <Tooltip title="Export Excel" arrow>
        <IconButton color="success" onClick={() => setShowDialog(true)}>
          <FileDownloadOutlined />
        </IconButton>
      </Tooltip>

      <AppConfirmDialog
        open={showDialog}
        onClose={handleReset}
        onConfirm={handleSubmit(handleDownload)}
        title="Download Vendor Profile Excel"
        dialogIcon={() => <FileDownloadOutlined color="success" />}
        fullWidth
      >
        <AppForm loading={isBusy}>
          <AppForm.Content>
            <Grid container spacing={2}>
              <Grid item xs={6}>
                <Controller
                  name="responsibleUser"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <UserSearchAutocomplete
                      label="Onboarded By"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="addedByUser"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <UserSearchAutocomplete
                      label="Added By"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="referredByUser"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <UserSearchAutocomplete
                      label="Referred By"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Grid>
              <Grid item xs={6}>
                <Controller
                  name="relationshipManagerUser"
                  control={control}
                  render={({ field: { value, onChange } }) => (
                    <UserSearchAutocomplete
                      label="Relationship Manager"
                      value={value}
                      onChange={onChange}
                    />
                  )}
                />
              </Grid>
            </Grid>
          </AppForm.Content>
        </AppForm>
        <Box sx={{ display: "flex", gap: 2, ml: 3 }}>
          <Controller
            name="dateRange"
            control={control}
            render={({ field, fieldState: { error } }) => (
              <DateRangeToggle {...field} label="Select Date Range" />
            )}
          />
        </Box>
      </AppConfirmDialog>
    </>
  );
};

export default ExportVendorProfileExcel;